import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Palette, X, ZoomIn, ChevronLeft, ChevronRight, Camera, User, Calendar, Maximize2 } from 'lucide-react';

import { API } from '@/config/api';

// Gallery collections - exhibition style
const collections = [
  {
    id: 'featured',
    name: 'Featured Works',
    description: 'Curated selection of exceptional pieces'
  },
  {
    id: 'photography',
    name: 'Photography',
    description: 'Capturing moments that speak to the soul'
  },
  {
    id: 'digital',
    name: 'Digital Art',
    description: 'Where technology meets creativity'
  },
  {
    id: 'mixed-media',
    name: 'Mixed Media',
    description: 'Breaking boundaries in artistic expression'
  }
];

// Gallery pieces with more art-focused data
const galleryPieces = [
  {
    id: 'art_1',
    title: 'Golden Horizons',
    artist: 'Nouvelle Vie Fine Art',
    description: 'An exploration of wealth consciousness through abstract expressionism. The golden hues represent the abundant mindset required for generational wealth building.',
    image: 'https://images.pexels.com/photos/5726981/pexels-photo-5726981.jpeg',
    medium: 'Acrylic on Canvas',
    dimensions: '48" x 36"',
    year: 2024,
    collection: 'featured'
  },
  {
    id: 'art_2',
    title: 'Celestial Currency',
    artist: 'Nouvelle Vie Fine Art',
    description: 'A mixed media piece exploring the transition from traditional to digital wealth. Incorporating actual currency fragments with digital art techniques.',
    image: 'https://images.pexels.com/photos/3109807/pexels-photo-3109807.jpeg',
    medium: 'Mixed Media',
    dimensions: '36" x 24"',
    year: 2024,
    collection: 'mixed-media'
  },
  {
    id: 'art_3',
    title: 'Legacy in Motion',
    artist: 'Nouvelle Vie Fine Art',
    description: 'A photographic study of generational impact. Capturing the essence of legacy through movement and light.',
    image: 'https://images.pexels.com/photos/20967/pexels-photo.jpg',
    medium: 'Photography',
    dimensions: '40" x 30"',
    year: 2023,
    collection: 'photography'
  },
  {
    id: 'art_4',
    title: 'Digital Renaissance',
    artist: 'Nouvelle Vie Fine Art',
    description: 'Where classical art principles meet cutting-edge digital techniques. A bridge between artistic traditions and the future.',
    image: 'https://images.pexels.com/photos/1191710/pexels-photo-1191710.jpeg',
    medium: 'Digital Art',
    dimensions: '60" x 40"',
    year: 2024,
    collection: 'digital'
  },
  {
    id: 'art_5',
    title: 'Emergence',
    artist: 'Nouvelle Vie Fine Art',
    description: 'Symbolizing the rise of new wealth builders. Abstract forms emerging from darkness into gold-touched light.',
    image: 'https://images.pexels.com/photos/1585325/pexels-photo-1585325.jpeg',
    medium: 'Oil on Canvas',
    dimensions: '52" x 40"',
    year: 2024,
    collection: 'featured'
  },
  {
    id: 'art_6',
    title: 'Urban Prosperity',
    artist: 'Nouvelle Vie Fine Art',
    description: 'A photographic exploration of wealth in urban landscapes. Finding beauty in the architecture of success.',
    image: 'https://images.pexels.com/photos/1105766/pexels-photo-1105766.jpeg',
    medium: 'Photography',
    dimensions: '36" x 24"',
    year: 2023,
    collection: 'photography'
  },
  {
    id: 'art_7',
    title: 'The Network',
    artist: 'Nouvelle Vie Fine Art',
    description: 'Digital visualization of global financial connections. Data becomes art in this stunning piece.',
    image: 'https://images.pexels.com/photos/373543/pexels-photo-373543.jpeg',
    medium: 'Digital Art',
    dimensions: '48" x 32"',
    year: 2024,
    collection: 'digital'
  },
  {
    id: 'art_8',
    title: 'Foundation',
    artist: 'Nouvelle Vie Fine Art',
    description: 'Exploring the building blocks of generational wealth through geometric abstraction.',
    image: 'https://images.pexels.com/photos/1647116/pexels-photo-1647116.jpeg',
    medium: 'Mixed Media',
    dimensions: '44" x 44"',
    year: 2023,
    collection: 'mixed-media'
  }
];

export default function ArtGallery() {
  const [selectedCollection, setSelectedCollection] = useState('featured');
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  const filteredPieces = selectedCollection === 'featured' 
    ? galleryPieces.filter(p => p.collection === 'featured')
    : galleryPieces.filter(p => p.collection === selectedCollection);
  
  const allPieces = galleryPieces;
  
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  
  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < allPieces.length) {
      setLightboxIndex(newIndex);
    }
  };
  
  const currentLightboxPiece = allPieces[lightboxIndex];
  
  return (
    <div className="min-h-screen bg-[#0d0c0a]" data-testid="art-gallery">
      {/* Hero - Tech Artsy Design */}
      <section className="relative h-[70vh] overflow-hidden pt-20">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(https://images.pexels.com/photos/3109807/pexels-photo-3109807.jpeg)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0c0a] via-[#0d0c0a]/70 to-[#0d0c0a]/30" />
          {/* Grid overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `
              linear-gradient(to right, #D4AF37 1px, transparent 1px),
              linear-gradient(to bottom, #D4AF37 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }} />
          {/* Glowing effect */}
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 h-full flex flex-col justify-end px-4 sm:px-6 lg:px-8 pb-20 max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full w-fit mb-6">
            <Palette className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-mono uppercase tracking-wider">Exhibition Gallery</span>
          </div>
          
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#FAFAFA] mb-4">
            Nouvelle Vie<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F5E6A3]">
              Fine Art
            </span>
          </h1>
          
          <p className="font-body text-xl text-[#FAFAFA]/70 max-w-2xl">
            A curated collection celebrating wealth, legacy, and the artistic expression of success. 
            Every piece tells a story of ambition and achievement.
          </p>
        </div>
      </section>
      
      {/* Collection Tabs */}
      <section className="sticky top-20 z-40 bg-[#0d0c0a]/95 backdrop-blur-md border-y border-[#D4AF37]/20 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={selectedCollection} onValueChange={setSelectedCollection}>
            <TabsList className="bg-transparent gap-2 flex-wrap h-auto">
              {collections.map((col) => (
                <TabsTrigger 
                  key={col.id}
                  value={col.id}
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814] bg-[#1c1814] text-[#FAFAFA] border border-[#D4AF37]/20 px-6 py-3 hover:border-[#D4AF37]/50 transition-all"
                  data-testid={`collection-${col.id}`}
                >
                  {col.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </section>
      
      {/* Collection Description */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#0d0c0a]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl text-[#FAFAFA] mb-2">
              {collections.find(c => c.id === selectedCollection)?.name}
            </h2>
            <p className="font-body text-[#FAFAFA]/60">
              {collections.find(c => c.id === selectedCollection)?.description}
            </p>
          </div>
        </div>
      </section>
      
      {/* Gallery Grid - Masonry Style */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0d0c0a]">
        <div className="max-w-7xl mx-auto">
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            {(selectedCollection === 'featured' ? allPieces : filteredPieces).map((piece, index) => (
              <div 
                key={piece.id}
                className="break-inside-avoid group cursor-pointer"
                onClick={() => openLightbox(allPieces.findIndex(p => p.id === piece.id))}
                data-testid={`gallery-piece-${piece.id}`}
              >
                <div className="relative overflow-hidden bg-[#1c1814]">
                  <img
                    src={piece.image}
                    alt={piece.title}
                    className="w-full object-cover transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                    style={{ aspectRatio: index % 3 === 0 ? '3/4' : index % 2 === 0 ? '4/3' : '1/1' }}
                  />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1c1814] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <Badge className="mb-2 bg-[#D4AF37]/90 text-[#1c1814]">{piece.medium}</Badge>
                      <h3 className="font-display text-xl text-[#FAFAFA] mb-1">{piece.title}</h3>
                      <p className="text-sm text-[#FAFAFA]/70">{piece.artist}</p>
                    </div>
                    <button className="absolute top-4 right-4 p-2 bg-[#FAFAFA] rounded-full">
                      <Maximize2 className="h-5 w-5 text-[#1c1814]" />
                    </button>
                  </div>
                </div>
                
                {/* Caption below image */}
                <div className="pt-4 pb-2">
                  <h3 className="font-display text-lg text-[#FAFAFA]">{piece.title}</h3>
                  <p className="text-sm text-[#FAFAFA]/60">{piece.medium}, {piece.year}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-6xl bg-[#1c1814] border-[#D4AF37]/20 p-0 overflow-hidden">
          {currentLightboxPiece && (
            <div className="grid lg:grid-cols-2">
              {/* Image */}
              <div className="relative aspect-square lg:aspect-auto lg:h-[80vh] bg-black">
                <img
                  src={currentLightboxPiece.image}
                  alt={currentLightboxPiece.title}
                  className="w-full h-full object-contain"
                />
                
                {/* Navigation Arrows */}
                <button 
                  onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
                  disabled={lightboxIndex === 0}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-[#1c1814]/80 hover:bg-[#D4AF37] text-[#FAFAFA] hover:text-[#1c1814] transition-colors disabled:opacity-30"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
                  disabled={lightboxIndex === allPieces.length - 1}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-[#1c1814]/80 hover:bg-[#D4AF37] text-[#FAFAFA] hover:text-[#1c1814] transition-colors disabled:opacity-30"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-[#1c1814]/80 text-[#FAFAFA] text-sm">
                  {lightboxIndex + 1} / {allPieces.length}
                </div>
              </div>
              
              {/* Details Panel */}
              <div className="p-8 lg:p-12 flex flex-col">
                <div className="flex-1">
                  <Badge className="mb-4 bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30">
                    {currentLightboxPiece.medium}
                  </Badge>
                  
                  <h2 className="font-display text-3xl lg:text-4xl text-[#FAFAFA] mb-2">
                    {currentLightboxPiece.title}
                  </h2>
                  
                  <p className="text-[#D4AF37] font-body mb-6">
                    by {currentLightboxPiece.artist}
                  </p>
                  
                  <p className="text-[#FAFAFA]/80 font-body leading-relaxed mb-8">
                    {currentLightboxPiece.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-6 text-sm">
                    <div>
                      <p className="text-[#FAFAFA]/50 mb-1 flex items-center gap-2">
                        <Palette className="h-4 w-4" /> Medium
                      </p>
                      <p className="text-[#FAFAFA]">{currentLightboxPiece.medium}</p>
                    </div>
                    <div>
                      <p className="text-[#FAFAFA]/50 mb-1 flex items-center gap-2">
                        <Maximize2 className="h-4 w-4" /> Dimensions
                      </p>
                      <p className="text-[#FAFAFA]">{currentLightboxPiece.dimensions}</p>
                    </div>
                    <div>
                      <p className="text-[#FAFAFA]/50 mb-1 flex items-center gap-2">
                        <User className="h-4 w-4" /> Artist
                      </p>
                      <p className="text-[#FAFAFA]">{currentLightboxPiece.artist}</p>
                    </div>
                    <div>
                      <p className="text-[#FAFAFA]/50 mb-1 flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> Year
                      </p>
                      <p className="text-[#FAFAFA]">{currentLightboxPiece.year}</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-8 border-t border-[#D4AF37]/20 mt-8">
                  <p className="text-[#FAFAFA]/60 text-sm mb-4">
                    Interested in this piece or commissioning similar work?
                  </p>
                  <Button 
                    onClick={() => window.open('mailto:dporterfield@nouveaurichelegacy.com?subject=Gallery Inquiry: ' + currentLightboxPiece.title, '_blank')}
                    className="btn-gold w-full"
                  >
                    Inquire About This Piece
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Artist Submission CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1c1814]">
        <div className="max-w-4xl mx-auto text-center">
          <Palette className="h-12 w-12 text-[#D4AF37] mx-auto mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl text-[#FAFAFA] mb-4">
            Submit Your <span className="text-[#D4AF37]">Portfolio</span>
          </h2>
          <p className="font-body text-[#FAFAFA]/70 mb-8 max-w-2xl mx-auto">
            Are you an artist looking to showcase your work? We're always looking for 
            talented creators who align with our vision of wealth, culture, and legacy.
          </p>
          <Button 
            onClick={() => window.open('mailto:dporterfield@nouveaurichelegacy.com?subject=Portfolio Entry', '_blank')}
            className="btn-gold"
            data-testid="submit-portfolio-btn"
          >
            <Camera className="mr-2 h-5 w-5" />
            Submit Portfolio
          </Button>
        </div>
      </section>
    </div>
  );
}
